import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { Box } from "@mui/system";
import { ClearIcon } from "@mui/x-date-pickers";
import * as yup from "yup";

const taskSchema = yup.object().shape({
  name: yup.string().required("Task Name is required"),
  description: yup.string().required("Description is required"),
  assignedTo: yup.string().required("Assigned To is required"),
  dueDate: yup.string().required("Due Date is required"),
});

const defaultTask = {
  name: "",
  description: "",
  assignedTo: "",
  status: "Pending",
  dueDate: "",
};

const TaskFormDialog = ({
  open,
  onClose,
  onSave,
  editingTask,
  isOwner,
  advocates,
  selectedCase,
  loading,
}) => {
  const [formData, setFormData] = useState(defaultTask);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setFormData(editingTask ? { ...editingTask } : { ...defaultTask });
      setErrors({});
    }
  }, [open, editingTask]);

  const handleSave = async () => {
    try {
      await taskSchema.validate(formData, { abortEarly: false });
      onSave(formData);
    } catch (error) {
      const validationErrors = {};
      error?.inner?.forEach((err) => {
        validationErrors[err.path] = err.message;
      });
      setErrors(validationErrors);
    }
  };

  return (
    <Dialog
      sx={{ "& .MuiPaper-root": { borderRadius: 4, boxShadow: 24, p: 1, width: 500 } }}
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{ fontSize: "18px", fontWeight: "bold", borderBottom: "1px solid #e0e0e0" }}
      >
        {editingTask ? "Edit Task" : "Add New Task"}
      </DialogTitle>
      <DialogContent sx={{ mt: "12px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "4px",
            paddingTop: "6px",
          }}
        >
          <TextField
            label="Task Name"
            fullWidth
            size="small"
            value={formData.name}
            error={!!errors.name}
            helperText={errors.name}
            required
            variant="outlined"
            disabled={!isOwner}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, name: e.target.value }));
              setErrors((prev) => ({ ...prev, name: "" }));
            }}
            InputProps={{
              sx: {
                bgcolor: "white",
                fontSize: "14px",
                "& input:-webkit-autofill": {
                  backgroundColor: "white !important",
                  WebkitBoxShadow: "0 0 0 30px white inset !important",
                  WebkitTextFillColor: "black !important",
                },
              },
            }}
            InputLabelProps={{ sx: { fontSize: "14px" } }}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            required
            rows={3}
            value={formData.description}
            error={!!errors.description}
            helperText={errors.description}
            variant="outlined"
            disabled={!isOwner}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, description: e.target.value }));
              setErrors((prev) => ({ ...prev, description: "" }));
            }}
            InputProps={{
              sx: {
                bgcolor: "white",
                fontSize: "14px",
                "& input:-webkit-autofill": {
                  backgroundColor: "white !important",
                  WebkitBoxShadow: "0 0 0 30px white inset !important",
                  WebkitTextFillColor: "black !important",
                },
              },
            }}
            InputLabelProps={{ sx: { fontSize: "14px" } }}
          />

          <FormControl fullWidth>
            <Autocomplete
              size="small"
              disabled={!isOwner}
              value={
                advocates?.find((a) => a.userId === formData.assignedTo) || null
              }
              clearIcon={formData.assignedTo ? <ClearIcon fontSize="small" /> : null}
              options={selectedCase?.lawyerDetails || []}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                return options.filter((option) =>
                  option.name.toLowerCase().includes(inputValue.toLowerCase())
                );
              }}
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  assignedTo: newValue?.userId || "",
                }));
                setErrors((prev) => ({ ...prev, assignedTo: "" }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ fontSize: "14px", "& .MuiInputBase-input": { fontSize: "14px" } }}
                  label="Assigned To *"
                  variant="outlined"
                  error={!!errors.assignedTo}
                  helperText={errors.assignedTo}
                  InputLabelProps={{ sx: { fontSize: "14px" } }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "14px",
                      maxHeight: "120px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": { width: "4px" },
                      "::-webkit-scrollbar-track": { background: "#fff" },
                      "::-webkit-scrollbar-thumb": { background: "#bbb", borderRadius: "4px" },
                    },
                  },
                },
              }}
              noOptionsText={<span style={{ fontSize: "14px" }}>No advocate found</span>}
            />
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              size="small"
              disabled={!isOwner}
              onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
              sx={{
                bgcolor: "white",
                fontSize: "14px",
                "& input:-webkit-autofill": {
                  backgroundColor: "white !important",
                  WebkitBoxShadow: "0 0 0 30px white inset !important",
                  WebkitTextFillColor: "black !important",
                },
              }}
            >
              <MenuItem sx={{ bgcolor: "white", fontSize: "14px" }} value="Pending">
                Pending
              </MenuItem>
              <MenuItem sx={{ bgcolor: "white", fontSize: "14px" }} value="Completed">
                Completed
              </MenuItem>
              <MenuItem sx={{ fontSize: "14px" }} value="InProcess">
                In Progress
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            required
            label="Due Date"
            type="date"
            fullWidth
            size="small"
            disabled={!isOwner}
            value={formData.dueDate}
            error={!!errors.dueDate}
            helperText={errors.dueDate}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, dueDate: e.target.value }));
              setErrors((prev) => ({ ...prev, dueDate: "" }));
            }}
            InputLabelProps={{ shrink: true, sx: { fontSize: "14px" } }}
            InputProps={{
              sx: {
                bgcolor: "white",
                fontSize: "14px",
                "& input:-webkit-autofill": {
                  backgroundColor: "white !important",
                  WebkitBoxShadow: "0 0 0 30px white inset !important",
                  WebkitTextFillColor: "black !important",
                },
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{
            borderRadius: "24px",
            padding: "8px 24px",
            textTransform: "none",
            fontWeight: "bold",
          }}
          variant="outlined"
          color="error"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isOwner || loading}
          sx={{
            borderRadius: "24px",
            padding: "8px 24px",
            textTransform: "none",
            fontWeight: "bold",
            backgroundColor: "#003D78",
            "&:hover": { backgroundColor: "primary.main", color: "#fff" },
            transition: "background-color 0.3s ease-in-out",
          }}
          onClick={handleSave}
        >
          {editingTask ? "Save Changes" : "Add Task"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskFormDialog;
